import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, ExternalLink, Pencil, RefreshCw, Send, ShieldCheck, Tag, XCircle } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { userName, useUsers } from "../lib/hooks";
import { Chip, PageHeader, ReasonsList, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SUM6_STATUS, SUM6_STMT, fmtDateShort, fmtDateTime } from "../lib/labels";

const BACK_ROUTE = { project: (id) => `/proyectos/${id}`, contract: () => "/legal/contratos", corporate: () => "/control-corporativo" };

export default function ExecutiveSummary() {
  const { stype, entityId } = useParams();
  const { user } = useAuth();
  const { map } = useUsers();
  const qc = useQueryClient();
  const [drawer, setDrawer] = useState(null); // statement
  const [dlg, setDlg] = useState(null);
  const [f, setF] = useState({});
  const [err, setErr] = useState(null);

  const refresh = () => qc.invalidateQueries();
  const { data, isLoading } = useQuery({
    queryKey: ["summary", stype, entityId],
    queryFn: async () => (await api.get(`/summaries/entidad/${stype}/${entityId}`)).data,
  });
  const s = data?.summary;

  const mut = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body || {})).data,
    onSuccess: () => { toast.success("Operación aplicada"); setDlg(null); setF({}); setErr(null); refresh(); },
    onError: (e) => { setErr(e); toast.error(apiError(e) || "Error"); },
  });
  const open = (d) => { setDlg(d); setF({}); setErr(null); };
  const sact = (action, extra = {}) => mut.mutate({ path: `/summaries/${s.id}/accion`, body: { action, ...extra } });
  const tact = (stid, action, extra = {}) => mut.mutate({ path: `/summaries/${s.id}/statements/${stid}/accion`, body: { action, ...extra } });

  if (isLoading) return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" /></div>;

  return (
    <div className="space-y-4" data-testid="summary-page">
      <Link to={BACK_ROUTE[stype]?.(entityId) || "/"} className="text-xs font-medium text-blue-700 hover:underline" data-testid="summary-back">← Volver al origen</Link>

      {!s ? (
        <div className="ts-card mx-auto max-w-lg p-8 text-center" data-testid="summary-empty">
          <p className="font-display text-lg font-semibold">Aún no hay executive summary</p>
          <p className="mt-1 text-sm text-slate-500">Se genera de forma determinista desde los datos existentes. Toda afirmación conserva su fuente.</p>
          <Button className="mt-4" data-testid="summary-generate" onClick={() => mut.mutate({ path: "/summaries/generar", body: { summary_type: stype, entity_id: entityId } })}>Generar executive summary</Button>
        </div>
      ) : (
        <>
          <PageHeader title={s.title} testid="summary-title"
            subtitle={`v${s.version_label} · generado ${fmtDateTime(s.generated_at)} por ${userName(map, s.generated_by)} · datos actualizados: ${fmtDateTime(s.freshness.updated_at)} · confianza global ${Math.round(s.overall_confidence * 100)}%`}
            actions={<>
              <Chip map={SUM6_STATUS} value={s.status} testid="summary-status" />
              {s.freshness.status !== "actual" && <ToneChip label={`fuentes desactualizadas: ${(s.freshness.stale_sources || []).join(", ")}`} tone="warning" testid="summary-stale" />}
              <Button variant="outline" size="sm" data-testid="summary-regen" onClick={() => mut.mutate({ path: "/summaries/generar", body: { summary_type: stype, entity_id: entityId } })}><RefreshCw className="mr-1 h-3.5 w-3.5" /> Regenerar (nueva versión si hay cambios)</Button>
              {["generated", "needs_review"].includes(s.status) && <Button size="sm" data-testid="summary-send-review" onClick={() => sact("enviar_revision")}><Send className="mr-1 h-3.5 w-3.5" /> Enviar a revisión</Button>}
              {s.status === "in_review" && <>
                <Button size="sm" data-testid="summary-approve" onClick={() => sact("aprobar")}><ShieldCheck className="mr-1 h-3.5 w-3.5" /> Aprobar (v1.0 inmutable)</Button>
                <Button size="sm" variant="ghost" data-testid="summary-return" onClick={() => open({ kind: "devolver" })}>Devolver</Button>
              </>}
            </>} />

          {s.preflight && (
            <p className={`rounded-sm border p-2 text-xs ${s.preflight.status === "ready_for_review" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-700"}`} data-testid="summary-preflight">
              Preflight: {s.preflight.status === "ready_for_review" ? "READY FOR REVIEW — afirmaciones materiales con fuente, decisiones enlazadas, cifras reconciliadas" : "BLOCKED — revisa los motivos"}
              {s.preflight.reasons?.length > 0 && `: ${s.preflight.reasons.map((r) => r.message || r).join(" · ")}`}
            </p>
          )}

          {Object.entries(s.sections || groupBySection(s.statements)).map(([sec, items]) => (
            <section key={sec} className="ts-card p-4" data-testid={`sum-section-${sec}`}>
              <h2 className="ts-section-title mb-2">{sectionLabel(sec)}</h2>
              <ul className="space-y-1.5">
                {items.filter((x) => !x.rejected).map((x) => (
                  <li key={x.id} className="flex items-start gap-2 text-sm" data-testid={`stmt-${x.id}`}>
                    <Chip map={SUM6_STMT} value={x.statement_type} testid={`stmt-type-${x.id}`} />
                    {x.severity === "danger" && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#B91C1C]" title="crítico" />}
                    {x.severity === "warning" && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#B45309]" title="atención" />}
                    <button className="min-w-0 flex-1 text-left text-slate-700 hover:text-slate-900" data-testid={`stmt-open-${x.id}`} onClick={() => setDrawer(x)}>
                      {x.text}
                      {x.validated_by
                        ? <span className="ml-2 text-xs text-emerald-700">✓ validada por {userName(map, x.validated_by)}</span>
                        : <span className="ml-2 text-xs text-amber-700">pendiente de validación</span>}
                      {x.wording_edited && <span className="ml-1 text-xs text-slate-400">(redacción editada)</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <section className="ts-card p-4" data-testid="sum-section-sources">
            <h2 className="ts-section-title mb-2">Sources</h2>
            <ul className="space-y-1">
              {(s.sources || []).map((r, i) => (
                <li key={i}><Link to={r.route} className="text-sm text-blue-700 hover:underline" data-testid={`source-${i}`}><ExternalLink className="mr-1 inline h-3 w-3" />{r.label}</Link></li>
              ))}
            </ul>
          </section>
        </>
      )}

      {/* SourceDrawer: de la afirmación a la fuente */}
      <Dialog open={!!drawer} onOpenChange={() => setDrawer(null)}>
        <DialogContent data-testid="source-drawer">
          <DialogHeader><DialogTitle>Fuente de la afirmación</DialogTitle></DialogHeader>
          {drawer && (
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Chip map={SUM6_STMT} value={drawer.statement_type} />
                <p className="text-sm text-slate-700">{drawer.text}</p>
              </div>
              <p className="text-xs text-slate-500">Generada por: {drawer.generated_by} · confianza {Math.round((drawer.confidence || 0) * 100)}% ·
                {drawer.validated_by ? ` validada por ${userName(map, drawer.validated_by)} el ${fmtDateShort(drawer.validated_at)}` : " pendiente de validación humana"}</p>
              <div className="space-y-2" data-testid="drawer-sources">
                {(drawer.source_refs || []).length === 0 && <p className="text-xs text-red-700">Sin fuente — un FACT sin fuente bloquea el preflight.</p>}
                {(drawer.source_refs || []).map((r, i) => (
                  <div key={i} className="rounded-sm border border-slate-200 bg-slate-50 p-2 text-xs" data-testid={`drawer-source-${i}`}>
                    <p><strong>{r.label}</strong> · tipo {r.type} · {r.id}</p>
                    <Link to={r.route} className="font-medium text-blue-700 hover:underline" data-testid={`drawer-open-${i}`}>OPEN SOURCE →</Link>
                  </div>
                ))}
              </div>
              {drawer.comments?.length > 0 && (
                <ul className="space-y-1">{drawer.comments.map((c, i) => <li key={i} className="text-xs text-slate-500">{userName(map, c.by)}: {c.text}</li>)}</ul>
              )}
              {s && s.status !== "approved" && (
                <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3" data-testid="stmt-actions">
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="stmt-validate" onClick={() => { tact(drawer.id, "validar"); setDrawer(null); }}><Check className="mr-1 h-3 w-3" /> Validar</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="stmt-edit" onClick={() => open({ kind: "editar", st: drawer })}><Pencil className="mr-1 h-3 w-3" /> Editar redacción</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="stmt-reclass" onClick={() => open({ kind: "reclasificar", st: drawer })}><Tag className="mr-1 h-3 w-3" /> Reclasificar</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="stmt-reqsource" onClick={() => { tact(drawer.id, "solicitar_fuente"); setDrawer(null); }}>Solicitar fuente</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-700" data-testid="stmt-reject" onClick={() => { tact(drawer.id, "rechazar"); setDrawer(null); }}><XCircle className="mr-1 h-3 w-3" /> Rechazar</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="stmt-comment" onClick={() => open({ kind: "comentar", st: drawer })}>Comentar</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!dlg} onOpenChange={() => setDlg(null)}>
        <DialogContent data-testid="summary-dialog">
          <DialogHeader><DialogTitle>
            {dlg?.kind === "devolver" && "Devolver a revisión"}
            {dlg?.kind === "editar" && "Editar redacción (el dato fuente no cambia)"}
            {dlg?.kind === "reclasificar" && "Reclasificar afirmación"}
            {dlg?.kind === "comentar" && "Añadir comentario"}
          </DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              {dlg.kind === "devolver" && <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>}
              {dlg.kind === "editar" && (
                <>
                  <div><Label>Texto</Label><Input value={f.text ?? dlg.st.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="f-text" /></div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.meaning_changed} onChange={(e) => setF({ ...f, meaning_changed: e.target.checked })} data-testid="f-meaning" /> El cambio altera el significado material (exige nueva validación)</label>
                </>
              )}
              {dlg.kind === "reclasificar" && (
                <div><Label>Tipo</Label>
                  <Select value={f.statement_type || dlg.st.statement_type} onValueChange={(v) => setF({ ...f, statement_type: v })}>
                    <SelectTrigger data-testid="f-stype"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.keys(SUM6_STMT).map((k) => <SelectItem key={k} value={k}>{SUM6_STMT[k].label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {dlg.kind === "comentar" && <div><Label>Comentario *</Label><Input value={f.text || ""} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="f-comment" /></div>}
              {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="summary-error">{apiError(err)}</p>)}
              <Button data-testid="summary-dialog-submit" disabled={mut.isPending}
                onClick={() => {
                  if (dlg.kind === "devolver") sact("devolver", { motivo: f.motivo });
                  else if (dlg.kind === "editar") { tact(dlg.st.id, "editar_wording", { text: f.text ?? dlg.st.text, meaning_changed: !!f.meaning_changed }); setDrawer(null); }
                  else if (dlg.kind === "reclasificar") { tact(dlg.st.id, "reclasificar", { statement_type: f.statement_type }); setDrawer(null); }
                  else if (dlg.kind === "comentar") { tact(dlg.st.id, "comentar", { text: f.text }); setDrawer(null); }
                }}>Confirmar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function groupBySection(statements) {
  const out = {};
  for (const st of statements || []) (out[st.section] = out[st.section] || []).push(st);
  return out;
}

const SECTION_ORDER_LABEL = {
  executive_position: "1 · Executive Position", key_facts: "2 · Key Facts", financial_position: "3 · Financial Position",
  programme: "4 · Programme / Schedule", risks_blockers: "5 · Risks & Blockers", contractual_procurement: "6 · Contractual / Procurement",
  quality_permits: "7 · Quality / Permits", decisions_required: "8 · Decisions Required", next_30_days: "9 · Next 30 Days",
  sources: "10 · Sources", contract_position: "1 · Contract Position", parties: "3 · Parties",
  commercial_terms: "4 · Commercial Terms", key_obligations: "5 · Key Obligations", material_risks: "6 · Material Risks",
  deadlines: "7 · Deadlines", documents_signature: "8 · Documents / Signature", actions_required: "9 · Actions Required",
  executive_overview: "1 · Executive Overview", requires_attention: "2 · Requires Attention", projects: "3 · Projects",
  financial_approvals: "4 · Financial / Approvals", contractual: "5 · Contractual", documentation: "6 · Documentation",
  insurance: "7 · Insurance", people_continuity: "8 · People / Continuity", what_changed: "10 · What Changed",
};
function sectionLabel(k) { return SECTION_ORDER_LABEL[k] || k; }
